import React, { useState } from 'react';
import {
  Grid,
  TextField,
  MenuItem,
  Button,
  Box,
  Typography,
  Paper,
  IconButton,
  Autocomplete,
  Divider,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  InputAdornment
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  AttachFile as AttachFileIcon
} from '@mui/icons-material';

const GST_SLABS = [5, 12, 18, 28];

const BulkMaterialForm = ({
  formData,
  sites,
  departments,
  expenseCategories,
  materialItems,
  filteredExpenseCategories,
  filteredMaterialItems,
  billFile,
  paymentProofFile,
  onItemsChange,
  onFormDataChange,
  onFileChange,
  setFilteredExpenseCategories,
  setFilteredMaterialItems,
  allFieldSettings
}) => {
  const [items, setItems] = useState([]);
  const [currentItem, setCurrentItem] = useState({
    expenseCategoryId: '',
    materialItemId: '',
    quantity: '',
    unit: '',
    amount: '',
    gstPercentage: ''
  });
  const [error, setError] = useState('');

  const isGST = formData.billType === 'GST';

  // Derived: GST calculation for current item being added
  const itemGSTAmount = isGST && currentItem.amount && currentItem.gstPercentage
    ? parseFloat((parseFloat(currentItem.amount) * parseFloat(currentItem.gstPercentage) / 100).toFixed(2))
    : 0;
  const itemTotalAmount = isGST && currentItem.amount && currentItem.gstPercentage
    ? parseFloat((parseFloat(currentItem.amount) + itemGSTAmount).toFixed(2))
    : parseFloat(currentItem.amount || 0);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    onFormDataChange({ target: { name, value } });

    // Department change - filter expense categories
    if (name === 'departmentId') {
      setFilteredExpenseCategories(expenseCategories);
      setCurrentItem({
        expenseCategoryId: '',
        materialItemId: '',
        quantity: '',
        unit: '',
        amount: '',
        gstPercentage: ''
      });
    }

    // Reset GST slab on bill type change
    if (name === 'billType') {
      setCurrentItem(prev => ({ ...prev, gstPercentage: '' }));
    }
  };

  const handleCurrentItemChange = (e) => {
    const { name, value } = e.target;
    setCurrentItem(prev => ({ ...prev, [name]: value }));

    // Expense category change - filter materials
    if (name === 'expenseCategoryId') {
      const filtered = materialItems.filter(item =>
        item.departmentId === parseInt(formData.departmentId) &&
        item.expenseCategoryId === parseInt(value)
      );
      setFilteredMaterialItems(filtered);
      setCurrentItem(prev => ({
        ...prev,
        expenseCategoryId: value,
        materialItemId: '',
        unit: ''
      }));
    }

    // Material item change - auto-fill unit
    if (name === 'materialItemId') {
      const item = materialItems.find(i => i.id === parseInt(value));
      if (item) {
        setCurrentItem(prev => ({ ...prev, unit: item.unit || '' }));
      }
    }
  };

  const handleAddItem = () => {
    // Validation
    // Validation
const catSettings = allFieldSettings?.[parseInt(currentItem.expenseCategoryId)];

if (!currentItem.expenseCategoryId) {
  setError('Please select an expense category');
  return;
}
if (catSettings?.materialItemMode !== 'freetext' && catSettings?.materialItemMode !== 'hidden' && !currentItem.materialItemId) {
  setError('Please select a material item');
  return;
}
if ((!catSettings || catSettings.quantityRequired) && !currentItem.quantity) {
  setError('Quantity is required');
  return;
}
if ((!catSettings || catSettings.amountRequired) && !currentItem.amount) {
  setError('Amount is required');
  return;
}
if (isGST && (!catSettings || catSettings.gstSlabRequired) && !currentItem.gstPercentage) {
  setError('Please select a GST slab for this item');
  return;
}

    // Get names for display
    const expenseCategory = expenseCategories.find(c => c.id === parseInt(currentItem.expenseCategoryId));
    const materialItem = materialItems.find(m => m.id === parseInt(currentItem.materialItemId));

    // Calculate total for display in the list
    const baseAmt = parseFloat(currentItem.amount);
    const gstPct = isGST && currentItem.gstPercentage ? parseFloat(currentItem.gstPercentage) : 0;
    const gstVal = parseFloat((baseAmt * gstPct / 100).toFixed(2));
    const totalAmt = isGST ? parseFloat((baseAmt + gstVal).toFixed(2)) : baseAmt;

    const newItem = {
      ...currentItem,
      id: Date.now(),
      expenseCategoryName: expenseCategory?.category_name,
      materialItemName: materialItem?.itemName,
      baseAmount: isGST ? baseAmt : null,
      gstAmount: isGST ? gstVal : null,
      totalAmount: totalAmt,
      gstPercentage: isGST ? currentItem.gstPercentage : null
    };

    const updatedItems = [...items, newItem];
    setItems(updatedItems);
    onItemsChange(updatedItems);

    // Reset current item
    setCurrentItem({
      expenseCategoryId: '',
      materialItemId: '',
      quantity: '',
      unit: '',
      amount: '',
      gstPercentage: ''
    });
    setFilteredMaterialItems([]);
    setError('');
  };

  const handleRemoveItem = (itemId) => {
    const updatedItems = items.filter(item => item.id !== itemId);
    setItems(updatedItems);
    onItemsChange(updatedItems);
  };

  const calculateTotal = () => {
    return items.reduce((sum, item) => sum + (item.totalAmount || parseFloat(item.amount || 0)), 0);
  };

  return (
    <Grid container spacing={2}>
      {error && (
        <Grid item xs={12}>
          <Alert severity="error" onClose={() => setError('')}>{error}</Alert>
        </Grid>
      )}

      {/* Common Details Section */}
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
          📋 Bill Details (Common for All Items)
        </Typography>
        <Divider sx={{ mb: 2 }} />
      </Grid>

      {/* Site */}
      <Grid item xs={12}>
        <TextField
          select
          fullWidth
          label="Site *"
          name="siteId"
          value={formData.siteId}
          onChange={handleFormChange}
          required
        >
          {sites.map((site) => (
            <MenuItem key={site.id} value={site.id}>
              {site.siteName}
            </MenuItem>
          ))}
        </TextField>
      </Grid>

      {/* Department */}
      <Grid item xs={12} sm={6}>
        <TextField
          select
          fullWidth
          label="Department *"
          name="departmentId"
          value={formData.departmentId}
          onChange={handleFormChange}
          required
        >
          {departments.map((dept) => (
            <MenuItem key={dept.id} value={dept.id}>
              {dept.department_name}
            </MenuItem>
          ))}
        </TextField>
      </Grid>

      {/* Supplier Name */}
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Supplier Name *"
          name="supplierName"
          value={formData.supplierName}
          onChange={handleFormChange}
          required
        />
      </Grid>

      {/* Bill Number */}
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Bill Number *"
          name="billNumber"
          value={formData.billNumber}
          onChange={handleFormChange}
          required
        />
      </Grid>

      {/* Entry Date */}
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          type="date"
          label="Entry Date *"
          name="entryDate"
          value={formData.entryDate}
          onChange={handleFormChange}
          InputLabelProps={{ shrink: true }}
          required
        />
      </Grid>

      {/* Bill Type */}
      <Grid item xs={12} sm={6}>
        <FormControl component="fieldset">
          <FormLabel component="legend">Bill Type *</FormLabel>
          <RadioGroup
            row
            name="billType"
            value={formData.billType}
            onChange={handleFormChange}
          >
            <FormControlLabel value="GST" control={<Radio />} label="GST Bill" />
            <FormControlLabel value="NON_GST" control={<Radio />} label="Non-GST Bill" />
          </RadioGroup>
        </FormControl>
      </Grid>

      {/* Payment Source */}
      <Grid item xs={12} sm={6}>
        <FormControl component="fieldset">
          <FormLabel component="legend">Payment Source *</FormLabel>
          <RadioGroup
            row
            name="paymentSource"
            value={formData.paymentSource}
            onChange={handleFormChange}
          >
            <FormControlLabel value="COMPANY" control={<Radio />} label="Company" />
            <FormControlLabel value="SUPERVISOR" control={<Radio />} label="Supervisor" />
          </RadioGroup>
        </FormControl>
      </Grid>

      {/* Items Section */}
      <Grid item xs={12} sx={{ mt: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
          📦 Add Material Items
        </Typography>
        <Divider sx={{ mb: 2 }} />
      </Grid>

      {/* Current Item Entry */}
      <Grid item xs={12}>
        <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
          <Grid container spacing={2}>
            {/* Expense Category */}
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Expense Category *"
                name="expenseCategoryId"
                value={currentItem.expenseCategoryId}
                onChange={handleCurrentItemChange}
                disabled={!formData.departmentId}
                helperText={!formData.departmentId ? 'Select department first' : ''}
              >
                {filteredExpenseCategories.map((cat) => (
                  <MenuItem key={cat.id} value={cat.id}>
                    {cat.category_name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Material Item */}
            <Grid item xs={12} sm={6}>
              <Autocomplete
                fullWidth
                options={filteredMaterialItems}
                getOptionLabel={(option) => option.unit ? `${option.itemName} (${option.unit})` : option.itemName}
                value={filteredMaterialItems.find(item => item.id === parseInt(currentItem.materialItemId)) || null}
                onChange={(event, newValue) => {
                  handleCurrentItemChange({
                    target: {
                      name: 'materialItemId',
                      value: newValue ? newValue.id : ''
                    }
                  });
                }}
                disabled={!currentItem.expenseCategoryId}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Material Item *"
                    helperText={!currentItem.expenseCategoryId ? 'Select expense category first' : 'Type to search...'}
                  />
                )}
                isOptionEqualToValue={(option, value) => option.id === value?.id}
              />
            </Grid>

            {/* Quantity */}
            <Grid item xs={6} sm={3}>
              <TextField
                fullWidth
                type="number"
                label="Quantity *"
                name="quantity"
                value={currentItem.quantity}
                onChange={handleCurrentItemChange}
                inputProps={{ min: 0, step: 0.01 }}
              />
            </Grid>

            {/* Unit */}
            <Grid item xs={6} sm={3}>
              <TextField
                fullWidth
                label="Unit *"
                name="unit"
                value={currentItem.unit}
                onChange={handleCurrentItemChange}
                InputProps={{ readOnly: !!currentItem.unit && !!currentItem.materialItemId }}
                helperText={!currentItem.unit && currentItem.materialItemId ? 'Enter unit manually' : ''}
              />
            </Grid>

            {/* Amount */}
            <Grid item xs={12} sm={isGST ? 3 : 6}>
              <TextField
                fullWidth
                type="number"
                label={isGST ? 'Base Amount *' : 'Amount *'}
                name="amount"
                value={currentItem.amount}
                onChange={handleCurrentItemChange}
                inputProps={{ min: 0, step: 0.01 }}
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                }}
              />
            </Grid>

            {/* GST Slab — only for GST bill type */}
            {isGST && (
              <Grid item xs={12} sm={3}>
                <TextField
                  select
                  fullWidth
                  label="GST Slab *"
                  name="gstPercentage"
                  value={currentItem.gstPercentage}
                  onChange={handleCurrentItemChange}
                >
                  {GST_SLABS.map(slab => (
                    <MenuItem key={slab} value={slab}>{slab}%</MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}

            {/* GST Breakdown for current item */}
            {isGST && currentItem.amount && currentItem.gstPercentage && (
              <Grid item xs={12}>
                <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'white' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2" color="text.secondary">Base Amount</Typography>
                    <Typography variant="body2">₹{parseFloat(currentItem.amount).toLocaleString()}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2" color="text.secondary">GST ({currentItem.gstPercentage}%)</Typography>
                    <Typography variant="body2" color="text.secondary">+ ₹{itemGSTAmount.toLocaleString()}</Typography>
                  </Box>
                  <Divider sx={{ my: 0.5 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" fontWeight="bold">Total</Typography>
                    <Typography variant="body2" fontWeight="bold" color="primary.main">
                      ₹{itemTotalAmount.toLocaleString()}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
            )}

            {/* Add Item Button */}
            <Grid item xs={12}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddItem}
                fullWidth
                disabled={!formData.departmentId}
              >
                Add Item to List
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </Grid>

      {/* Items List */}
      {items.length > 0 && (
        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            Added Items ({items.length})
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Category</TableCell>
                  <TableCell>Material</TableCell>
                  <TableCell align="right">Qty</TableCell>
                  <TableCell>Unit</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell align="center">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>{item.expenseCategoryName}</TableCell>
                    <TableCell>{item.materialItemName}</TableCell>
                    <TableCell align="right">{item.quantity}</TableCell>
                    <TableCell>{item.unit}</TableCell>
                    <TableCell align="right">
                      ₹{(item.totalAmount || parseFloat(item.amount)).toLocaleString()}
                      {item.gstPercentage && (
                        <Typography variant="caption" display="block" color="text.secondary">
                          ₹{parseFloat(item.amount).toLocaleString()} + {item.gstPercentage}% GST
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleRemoveItem(item.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell colSpan={4} align="right">
                    <Typography variant="subtitle1" fontWeight="bold">
                      Total:
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="subtitle1" fontWeight="bold" color="primary">
                      ₹{calculateTotal().toLocaleString()}
                    </Typography>
                  </TableCell>
                  <TableCell />
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      )}

      {/* Bill Upload */}
      <Grid item xs={12} sx={{ mt: 2 }}>
        <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
          📎 Attachments
        </Typography>
        <Divider sx={{ mb: 2 }} />
      </Grid>

      <Grid item xs={12}>
        <Button
          variant="outlined"
          component="label"
          fullWidth
          startIcon={<AttachFileIcon />}
        >
          {billFile ? billFile.name : 'Upload Bill (Image/PDF) *'}
          <input
            type="file"
            hidden
            accept="image/*,.pdf"
            onChange={(e) => onFileChange(e, 'bill')}
            required
          />
        </Button>
      </Grid>

      {formData.paymentSource === 'SUPERVISOR' && (
        <Grid item xs={12}>
          <Button
            variant="outlined"
            component="label"
            fullWidth
            startIcon={<AttachFileIcon />}
          >
            {paymentProofFile ? paymentProofFile.name : 'Upload Payment Proof *'}
            <input
              type="file"
              hidden
              accept="image/*,.pdf"
              onChange={(e) => onFileChange(e, 'payment')}
              required
            />
          </Button>
        </Grid>
      )}

      {/* Remarks */}
      <Grid item xs={12}>
        <TextField
          fullWidth
          multiline
          rows={2}
          label="Remarks (Optional)"
          name="remarks"
          value={formData.remarks}
          onChange={handleFormChange}
          placeholder="Any additional notes..."
        />
      </Grid>

      {/* Validation Message */}
      {items.length === 0 && (
        <Grid item xs={12}>
          <Alert severity="warning">
            Please add at least one item before submitting
          </Alert>
        </Grid>
      )}
    </Grid>
  );
};

export default BulkMaterialForm;